import { useFormik } from 'formik';
import { useMemo, useState } from 'react';
import * as Yup from 'yup';
import { BsFillPatchQuestionFill } from 'react-icons/bs';

import Modal from '../../../../components/modal';
import Input from '../../../../components/input';
import EmptyState from '../../../../components/emptyState';
import { Drawer } from '../../../../components/Drawer';
import {
  showSuccessMessage,
  showErrorMessage,
  setFormikErrors,
} from '../../../../hooks/constants';

import close from '../../../../assets/svg-icons/closeBrand.svg';
import arrow from '../../../../assets/svg-icons/blueArrow.svg';
import editIcon from '../../../../assets/svg-icons/edit.svg';
import deleteIcon from '../../../../assets/svg-icons/deleteRed.svg';

export default function VpnServicesTab({
  filtered,
  selectedIds,
  toggleAll,
  toggleOne,
  onOpenCreate,
  onRefresh,
  onRequestDelete,
  projectId,
  apiEndPoints,
  setLoading,
  routerList,
  subnetList,
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [manageModalIndex, setManageModalIndex] = useState(null);

  const rows = useMemo(() => (Array.isArray(filtered) ? filtered : []), [filtered]);

  const rowIds = rows.map((row) => row.id || row.uuid).filter(Boolean);

  const allRowsSelected =
    rowIds.length > 0 && rowIds.every((id) => selectedIds.has(id));

  const closeManageModal = () => setManageModalIndex(null);

  const isActiveStatus = (item) =>
    String(item?.status || '').toUpperCase() === 'ACTIVE';

  const getRouterName = (routerId) => {
    const router = Array.isArray(routerList)
      ? routerList.find((r) => (r.id || r.uuid || r.router_id) === routerId)
      : null;

    return router?.name || routerId || '-';
  };

  const getSubnetName = (subnetId) => {
    const subnet = Array.isArray(subnetList)
      ? subnetList.find((s) => (s.id || s.uuid) === subnetId)
      : null;

    if (!subnetId) return '-';
    if (subnet?.name && subnet?.cidr) return `${subnet.name} (${subnet.cidr})`;

    return subnet?.name || subnet?.cidr || subnetId || '-';
  };

  const getVpnConnections = (service) => {
    if (Array.isArray(service?.vpn_connections)) {
      return (
        service.vpn_connections
          .map((x) => x.name || x.id || x.uuid)
          .filter(Boolean)
          .join(', ') || '-'
      );
    }

    if (Array.isArray(service?.connections)) {
      return (
        service.connections
          .map((x) => x.name || x.id || x.uuid)
          .filter(Boolean)
          .join(', ') || '-'
      );
    }

    return (
      service?.vpn_connections ||
      service?.connections ||
      service?.ipsec_site_connections ||
      service?.ipsecsiteconnections ||
      '-'
    );
  };

  const getIPv4 = (service) =>
    service?.external_v4_ip ||
    service?.external_ipv4 ||
    service?.public_v4_ip ||
    service?.public_ipv4 ||
    service?.local_side_public_ip?.ipv4 ||
    service?.local_side_public_ip_addresses?.ipv4 ||
    service?.ipv4 ||
    '-';

  const getIPv6 = (service) =>
    service?.external_v6_ip ||
    service?.external_ipv6 ||
    service?.public_v6_ip ||
    service?.public_ipv6 ||
    service?.local_side_public_ip?.ipv6 ||
    service?.local_side_public_ip_addresses?.ipv6 ||
    service?.ipv6 ||
    '-';

  const openEdit = (row) => {
    if (!isActiveStatus(row)) {
      showErrorMessage('Only ACTIVE VPN Services can be edited');
      return;
    }

    setEditing(row);
    setEditOpen(true);
    closeManageModal();
  };

  const editForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editing?.name || '',
      description: editing?.description || '',
      admin_state_up: true,
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
    }),
    onSubmit: async (values) => {
      if (!projectId || !editing) return;

      if (!isActiveStatus(editing)) {
        showErrorMessage('Only ACTIVE VPN Services can be edited');
        return;
      }

      setLoading(true);

      try {
        const payload = {
          name: values.name,
          admin_state_up: true,
        };

        if (values.description?.trim()) {
          payload.description = values.description;
        }

        await apiEndPoints.updateVpnService(
          projectId,
          editing.id || editing.uuid,
          payload,
        );

        showSuccessMessage('VPN Service updated');
        setEditOpen(false);
        setEditing(null);
        await onRefresh();
      } catch (error) {
        setFormikErrors(error, editForm.setErrors);
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <>
      <table className="w-full text-sm text-left overflow-hidden">
        <thead className="bg-white dark:bg-slate-800 px-3 md:px-4 border dark:border-[#c4c4c432] text-sm font-semibold">
          <tr>
            <th className="px-6 py-4 whitespace-nowrap">
              <input
                type="checkbox"
                checked={allRowsSelected}
                onChange={toggleAll}
              />
            </th>
            <th className="px-6 py-4 whitespace-nowrap">Name</th>
            <th className="px-6 py-4 whitespace-nowrap">Description</th>
            <th className="px-6 py-4 whitespace-nowrap"> Local Side Public IPs</th>
            <th className="px-6 py-4 whitespace-nowrap">Subnet</th>
            <th className="px-6 py-4 whitespace-nowrap">Router</th>
            <th className="px-6 py-4 whitespace-nowrap">Status</th>
            <th className="px-6 py-4 whitespace-nowrap">Actions</th>
          </tr>
        </thead>

        <tbody className="bg-white dark:bg-slate-800 text-black/50 dark:text-white/60 px-3 md:px-4 border rounded-b-md dark:border-[#c4c4c432] text-sm">
          {rows.length === 0 ? (
            <tr>
              <td className="px-6 py-10" colSpan={7}>
                <EmptyState
                  title="No VPN services yet"
                  description="Create a VPN Service to connect your VPN configuration to a router."
                  primaryAction={{ label: 'Add VPN Service', onClick: onOpenCreate }}
                  secondaryAction={{ label: 'Refresh', onClick: onRefresh }}
                />
              </td>
            </tr>
          ) : (
            rows.map((row, index) => {
              const id = row.id || row.uuid;
              const canEdit = isActiveStatus(row);
                

              return (
                <tr
                  key={id}
                  className="dark:hover:bg-[#ffffff05] hover:bg-brandBlue/25 whitespace-nowrap"
                >
                  <td className="text-start py-4 px-6">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(id)}
                      onChange={() => toggleOne(id)}
                    />
                  </td>

                  <td className="text-start py-4 px-6">
                    <button
                      type="button"
                      className="text-primaryBlue hover:underline"
                      onClick={() => {
                        setSelected(row);
                        setDetailsOpen(true);
                      }}
                    >
                      {row.name || '-'}
                    </button>
                  </td>

                  <td className="text-start py-4 px-6">{row.description || '-'}</td>
                  <td className="px-6 py-4">
                  <div className="flex flex-col gap-1">
                    <div>
                      <strong>IPv4:</strong>{' '}
                      {row.external_v4_ip || 'None'}
                    </div>

                    <div>
                      <strong>IPv6:</strong>{' '}
                      {row.external_v6_ip || 'None'}
                    </div>
                  </div>
                </td>
                  <td className="text-start py-4 px-6">{getSubnetName(row.subnet_id)}</td>
                  <td className="text-start py-4 px-6">{getRouterName(row.router_id)}</td>
                  <td className="text-start py-4 px-6">{row.status || '-'}</td>

                  <td className="text-start py-4 px-6 relative">
                    <button
                      className="text-white p-3 text-xs rounded bg-primaryBlue w-full md:w-fit"
                      onClick={() => setManageModalIndex(index)}
                      type="button"
                    >
                      Manage VPN Service
                    </button>

                    {manageModalIndex === index && (
                      <div className="w-full fixed h-screen bg-black/70 top-0 left-0 px-4 flex items-end md:items-center justify-center z-50">
                        <div className="bg-white dark:bg-[#141D26] rounded w-full md:w-[30%] dark:text-white max-h-[80%] mb-4 md:mb-0 overflow-auto">
                          <div className="flex justify-between items-center text-sm p-6 border-b dark:border-b-[#c4c4c432]">
                            <span className="dark:text-white text-black font-medium">
                              Manage "{row.name}"
                            </span>
                            <img
                              src={close}
                              onClick={closeManageModal}
                              className="h-5 cursor-pointer"
                              alt=""
                            />
                          </div>

                          <div className="p-4 grid gap-1">
                            <div
                              className={`flex items-center justify-between border-b dark:border-[#c4c4c424] last:border-none py-4 px-3 transition-all font-medium ${
                                canEdit
                                  ? 'text-black dark:text-white cursor-pointer hover:bg-primaryBlue/5'
                                  : 'text-gray-400 cursor-not-allowed opacity-50'
                              }`}
                              onClick={() => openEdit(row)}
                              title={
                                canEdit
                                  ? 'Edit VPN Service'
                                  : 'Only ACTIVE VPN Services can be edited'
                              }
                            >
                              <div className="flex items-center gap-2">
                                <img src={editIcon} alt="" className="h-4" />
                                <span>Edit VPN Service</span>
                              </div>
                              <img src={arrow} alt="" />
                            </div>

                            <div
                              className="flex items-center justify-between border-b dark:border-[#c4c4c424] last:border-none py-4 px-3 hover:bg-primaryBlue/5 transition-all text-red-500 font-medium cursor-pointer"
                              onClick={() => {
                                onRequestDelete(id, row.name || id, 'Delete VPN Service');
                                closeManageModal();
                              }}
                            >
                              <div className="flex items-center gap-2">
                                <img src={deleteIcon} alt="" className="h-4" />
                                <span>Delete VPN Service</span>
                              </div>
                              <img src={arrow} alt="" />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      <Modal
        title="Edit VPN Service"
        isVisible={editOpen && isActiveStatus(editing)}
        onClose={() => {
          setEditOpen(false);
          setEditing(null);
        }}
        size="md:w-[55%]"
      >
        <form className="grid gap-4" onSubmit={editForm.handleSubmit}>
          <Input
            label="Name *"
            name="name"
            value={editForm.values.name}
            onChange={editForm.handleChange}
            onBlur={editForm.handleBlur}
            onError={editForm.touched.name ? editForm.errors.name : null}
            isLabelIcon={true}
            iconTitle="The name of the VPN service."
          />

          <Input
            label="Description"
            name="description"
            value={editForm.values.description}
            onChange={editForm.handleChange}
            onBlur={editForm.handleBlur}
            onError={editForm.touched.description ? editForm.errors.description : null}
            isLabelIcon={true}
            iconTitle="A description for the VPN service."
          />

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              name="admin_state_up"
              checked
              readOnly
              disabled
            />
            <span className="text-sm flex items-center">
              Admin State Up
              <BsFillPatchQuestionFill
                className="text-sm text-primaryBlue ml-1 cursor-pointer"
                title="Admin state is always enabled."
              />
            </span>
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="px-4 py-2 text-xs border-[1.5px] border-[#c4c4c460] rounded-md cursor-pointer bg-white dark:bg-slate-800"
              onClick={() => {
                setEditOpen(false);
                setEditing(null);
              }}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="px-4 py-2 text-xs rounded-md cursor-pointer bg-primaryBlue text-white hover:opacity-90"
            >
              Save Changes
            </button>
          </div>
        </form>
      </Modal>

      <Drawer
        open={detailsOpen}
        title={selected?.name || 'VPN Service'}
        onClose={() => {
          setDetailsOpen(false);
          setSelected(null);
        }}
        width={620}
      >
        {selected ? (
          <div className="grid gap-6">
            <div className="flex items-start justify-between gap-3">
              <div className="text-xl font-semibold">{selected.name}</div>

              <button
                type="button"
                className={`text-xs rounded-md px-3 py-2 border border-[#c4c4c460] ${
                  isActiveStatus(selected)
                    ? 'cursor-pointer bg-white dark:bg-slate-800 hover:bg-brandBlue/10'
                    : 'cursor-not-allowed opacity-50 bg-gray-100 dark:bg-slate-800'
                }`}
                disabled={!isActiveStatus(selected)}
                onClick={() => openEdit(selected)}
                title={
                  isActiveStatus(selected)
                    ? 'Edit VPN Service'
                    : 'Only ACTIVE VPN Services can be edited'
                }
              >
                Edit VPN Service
                
              </button>
            </div>

            <div>
              <p className="text-lg font-semibold mb-3">VPN Service Details</p>

              <div className="border dark:border-[#c4c4c432] rounded-md overflow-hidden">
                {[
                  ['Name', selected.name],
                  ['Description', selected.description || '-'],
                  ['ID', selected.id || selected.uuid],
                  ['Project ID', selected.project_id],
                  ['Router', getRouterName(selected.router_id)],
                  ['Subnet', getSubnetName(selected.subnet_id)],
                  [
                    'Admin State',
                    selected.admin_state_up === true
                      ? 'UP'
                      : selected.admin_state_up === false
                        ? 'DOWN'
                        : selected.admin_state || '-',
                  ],
                  ['Status', selected.status || '-'],
                ].map(([key, val]) => (
                  <div
                    key={key}
                    className="grid grid-cols-3 gap-3 px-4 py-3 border-b last:border-b-0 dark:border-[#c4c4c432]"
                  >
                    <div className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                      {key}
                    </div>

                    <div className="col-span-2 text-sm text-gray-900 dark:text-white">
                      {val ?? '-'}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="text-lg font-semibold mb-3">
                Local Side Public IP Addresses
              </p>

              <div className="border dark:border-[#c4c4c432] rounded-md overflow-hidden">
                {[
                  ['IPv4', getIPv4(selected)],
                  ['IPv6', getIPv6(selected)],
                ].map(([key, val]) => (
                  <div
                    key={key}
                    className="grid grid-cols-3 gap-3 px-4 py-3 border-b last:border-b-0 dark:border-[#c4c4c432]"
                  >
                    <div className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                      {key}
                    </div>

                    <div className="col-span-2 text-sm text-gray-900 dark:text-white">
                      {val ?? '-'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-sm opacity-70">No VPN service selected.</div>
        )}
      </Drawer>
    </>
  );
}
